import { getLanguages, getNavLinkProps } from '../../helpers/getPageProps';

/**
 * Recursively maps menu items to navigation link props, including sub-links.
 *
 * @param {NavLinkApiProps[]} items - The menu items to map.
 * @returns {NavLinkProps[]} The mapped navigation link props.
 */
const getMainNavProps = (items) => {
  if (!items) return [];
  return items.map((extendedLink) => ({
    ...getNavLinkProps(extendedLink),
    subLinks: extendedLink.subLinks
      ? getMainNavProps(extendedLink.subLinks)
      : [],
  }));
};

/**
 * Generates the props for the header component based on page and site content.
 *
 * @param {CareerPageApiProps} pageContent - The content data for the current page.
 * @param {CareerSiteApiProps} siteContent - The site-wide content data.
 * @param {boolean} [withHeader=true] - Whether to include the header or return an empty/default header.
 * @returns {HeaderProps} The props for the header component.
 */
export const getHeaderProps = (pageContent, siteContent, withHeader = true) => {
  if (!withHeader) {
    return {
      headerType: 'big',
      mainNavigationProps: {
        headerTopProps: {
          logoLink: {
            text: '',
            url: '/',
            target: '_self',
          },
          language: {
            activeLanguage: pageContent.language,
            languages: getLanguages(pageContent),
          },
          cta: siteContent.primaryCtaLink
            ? getNavLinkProps(siteContent.primaryCtaLink)
            : undefined,
        },
        mainNavProps: [],
        channels: [],
      },
      jobEditorLinks: siteContent?.jobEditorLinks,
      notification: siteContent?.notification || undefined,
    };
  }

  return {
    headerType: 'big',
    mainNavigationProps: {
      headerTopProps: {
        logoLink: {
          text: '',
          url: '/',
          target: '_self',
        },
        language: {
          activeLanguage: pageContent.language,
          languages: getLanguages(pageContent),
        },
        cta: siteContent.primaryCtaLink
          ? getNavLinkProps(siteContent.primaryCtaLink)
          : undefined,
      },
      mainNavProps: getMainNavProps(siteContent.menu),
      channels: siteContent.socialMedia?.links || [],
    },
    jobEditorLinks: siteContent?.jobEditorLinks || undefined,
    notification: siteContent?.notification || undefined,
  };
};

/**
 * Maps groups of link definitions to arrays of navigation link props.
 *
 * @param {Array<Array<LinkDefApiProps>>} groups - The link groups to map.
 * @returns {NavLinkProps[][]} The mapped navigation link groups.
 */
const getFooterLinks = (groups) => {
  /** @type {NavLinkProps[][]} */
  const links = [];

  groups.map((group) => {
    links.push(group.map((link) => getNavLinkProps(link)));
  });
  return links;
};

/**
 * Generates the props for the footer component based on site content and internationalization.
 *
 * @param {CareerSiteApiProps} siteContent - The site-wide content data.
 * @param {import("react-intl").IntlShape} intl - The react-intl object for formatting messages.
 * @param {boolean} [minimalFooter=false] - Whether to generate a minimal footer.
 * @returns {FooterBigProps} The props for the footer component.
 */
export const getFooterProps = (siteContent, intl, minimalFooter = false) => {
  if (minimalFooter) {
    return {
      logoLink: {
        text: '',
        url: '/',
      },
      footerLinks: [],
      socialMediaButtons: {
        channels: [],
      },
      footerSmallProps: {
        links: siteContent.secondaryLinks.map((link) => getNavLinkProps(link)),
        copyright: {
          text: intl.formatMessage({
            id: 'landingpage.copytext',
          }),
          url: '/',
        },
      },
    };
  }

  const footerCta = siteContent.secondaryCta
    ? {
        headline: siteContent.secondaryCta.title,
        cta: getNavLinkProps(siteContent.secondaryCta.link),
      }
    : undefined;
  return {
    logoLink: {
      text: '',
      url: '/',
    },
    footerLinks: getFooterLinks(siteContent.linkGroups),
    socialMediaButtons: {
      socialMediaLink: getNavLinkProps(siteContent.socialMedia.overviewLink),
      channels: siteContent.socialMedia.links,
    },
    footerSmallProps: {
      links: siteContent.secondaryLinks.map((link) => getNavLinkProps(link)),
      copyright: {
        text: intl.formatMessage({
          id: 'landingpage.copytext',
        }),
        url: '/',
      },
    },
    footerCta,
  };
};
